// cspell:ignore conv
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { ArrowLeft, PanelLeft, Plus, Search, Send } from "lucide-react";
import { GiftCardButton } from "@/components/GiftCardButton";
import {
    AutoGrowTextarea,
    Avatar,
    AvatarGradientFallback,
    Button,
    ChatDateDivider,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    isNewDay,
    MessageBubble,
    Mono,
    resolveMessageSender,
    Text,
    useIsMobile,
} from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { CurrentUserDocument } from "../graphql/auth.generated";
import {
    CoachChatsDocument,
    CreateChatDocument,
    JoinChatDocument,
    MarkChatReadInboxDocument,
    SendChatMessageDocument,
    type CoachChatsQuery,
} from "../graphql/chat.generated";
import { UsersDocument } from "../graphql/users.generated";
import { useAllRows } from "@/hooks/useAllRows";
import { hasUnreadToMark, useChatLiveUpdates } from "@athena/realtime";
import { getEnv } from "../lib/env";

/* ─────────────────────────────── types ──────────────────────────────────── */

type ConversationStatus = "unread" | "after_hours" | "crisis" | "quiet" | null;

interface Conversation {
    id: string;
    name: string;
    initials: string;
    org: string;
    preview: string;
    time: string;
    unreadCount?: number;
    status?: ConversationStatus;
    dayLabel?: string;
}

interface ThreadMessage {
    id: string;
    body: string;
    authorId: string | null;
    senderKind: string;
    createdAt: string;
}

type FilterTab = "all" | "unread";

interface ChatUser {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    memberships?: { organization: { id: string; name: string } }[];
}

function organizationFor(user: ChatUser | undefined): string {
    return user?.memberships?.[0]?.organization.name ?? "";
}

function displayName(user: ChatUser | undefined): string {
    if (!user) return "Unknown";
    const full = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
    return full || user.email || "Unknown";
}

function initialsFor(user: ChatUser | undefined): string {
    if (!user) return "?";
    const first = user.firstName?.[0] ?? "";
    const last = user.lastName?.[0] ?? "";
    const initials = `${first}${last}`.toUpperCase();
    return initials || (user.email?.[0]?.toUpperCase() ?? "?");
}

function relativeTime(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const diffMs = Date.now() - then;
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "Yest";
    if (days < 7) return `${days} days`;
    return new Date(iso).toLocaleDateString();
}

function messageTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** Merge persisted + live-appended messages, dedupe by id, sort by createdAt. */
function mergeMessages(base: ThreadMessage[], live: ThreadMessage[]): ThreadMessage[] {
    const byId = new Map<string, ThreadMessage>();
    for (const m of [
        ...base,
        ...live,
    ])
        byId.set(m.id, m);
    return [...byId.values()].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

function StatusPill({ status }: { status: ConversationStatus }) {
    if (!status) return null;
    if (status === "after_hours")
        return (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wide text-amber-700 uppercase dark:bg-amber-950 dark:text-amber-400">
                After Hours
            </span>
        );
    if (status === "crisis")
        return (
            <span className="rounded bg-rose-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wide text-rose-700 uppercase dark:bg-rose-950 dark:text-rose-400">
                Crisis
            </span>
        );
    if (status === "quiet")
        return (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wide text-slate-500 uppercase dark:bg-slate-800 dark:text-slate-400">
                Quiet
            </span>
        );
    return null;
}

function ConversationRow({
    conv: conversation,
    selected,
    onClick,
}: {
    conv: Conversation;
    selected: boolean;
    onClick: () => void;
}) {
    return (
        <button
            data-chat-id={conversation.id}
            onClick={onClick}
            className={cn(
                "flex w-full items-start gap-3 border-b px-4 py-3 text-left transition-colors",
                selected
                    ? "border-l-secondary bg-secondary/5 border-l-2"
                    : "border-border hover:bg-muted/40 border-l-2 border-l-transparent",
            )}
        >
            <Avatar size="sm" className="mt-0.5 shrink-0">
                <AvatarGradientFallback>{conversation.initials}</AvatarGradientFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
                <div className="mb-0.5 flex items-center justify-between gap-1">
                    <div className="flex min-w-0 items-center gap-1.5">
                        <Text weight="semibold" className="truncate text-sm">
                            {conversation.name}
                        </Text>
                        {conversation.status && <StatusPill status={conversation.status} />}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                        <Mono className="text-muted-foreground text-[11px]">{conversation.time}</Mono>
                        {conversation.unreadCount ? (
                            <span className="bg-primary text-primary-foreground flex h-4.5 w-4.5 items-center justify-center rounded-full text-[10px] font-semibold">
                                {conversation.unreadCount}
                            </span>
                        ) : null}
                    </div>
                </div>
                <div className="flex items-center justify-between gap-1">
                    <Text className="text-muted-foreground truncate text-xs">{conversation.org}</Text>
                    {conversation.dayLabel && (
                        <Mono className="text-muted-foreground shrink-0 text-[10px]">{conversation.dayLabel}</Mono>
                    )}
                </div>
                <Text className="text-muted-foreground mt-0.5 truncate text-xs">{conversation.preview}</Text>
            </div>
        </button>
    );
}

/* ─────────────────────────────── main page ─────────────────────────────── */

export function InboxPage() {
    const [
        selectedId,
        setSelectedId,
    ] = useState<string>("");
    const [
        filter,
        setFilter,
    ] = useState<FilterTab>("all");
    const [
        search,
        setSearch,
    ] = useState("");
    const [
        messageText,
        setMessageText,
    ] = useState("");
    // Live + optimistic messages appended on top of the query result, per chat.
    const [
        liveByChat,
        setLiveByChat,
    ] = useState<Record<string, ThreadMessage[]>>({});

    const [
        newConvOpen,
        setNewConvOpen,
    ] = useState(false);
    const [
        newConvSearch,
        setNewConvSearch,
    ] = useState("");
    const [
        newConvUserId,
        setNewConvUserId,
    ] = useState<string | null>(null);
    const [
        newConvCreating,
        setNewConvCreating,
    ] = useState(false);

    const { data: meData } = useQuery(CurrentUserDocument);
    /*
     * The newest conversations first, with the rest arriving behind them.
     *
     * Each chat carries its full message history, so the unpaged query read every message on the
     * platform before the list could render a single row. A smaller page than the default: the
     * point is the first paint, and 25 conversations fill the pane.
     */
    const {
        rows: chats,
        initialLoading,
        pagingMore,
        refetch,
    } = useAllRows<CoachChatsQuery["chats"][number]>(
        CoachChatsDocument,
        (d) => (d as CoachChatsQuery | undefined)?.chats,
        { pageSize: 25 },
    );
    const { data: usersData } = useQuery(UsersDocument, { variables: { limit: 200 } });
    const [sendMessage] = useMutation(SendChatMessageDocument);
    const [markRead] = useMutation(MarkChatReadInboxDocument);
    const [createChat] = useMutation(CreateChatDocument);
    const [joinChat] = useMutation(JoinChatDocument);

    const me = meData?.currentUser;

    // Live updates are only available when AppSync is configured.
    const liveEnabled = !!getEnv("VITE_APPSYNC_GRAPHQL_URL");

    const appendLive = (chatId: string, msg: ThreadMessage) =>
        setLiveByChat((prev) => {
            const existing = prev[chatId] ?? [];
            if (existing.some((m) => m.id === msg.id)) return prev;
            return {
                ...prev,
                [chatId]: [
                    ...existing,
                    msg,
                ],
            };
        });

    useChatLiveUpdates({
        chatId: selectedId || undefined,
        enabled: liveEnabled,
        onMessage: (m) =>
            appendLive(m.chatId, {
                id: m.id,
                body: m.body,
                authorId: m.authorId,
                senderKind: m.senderKind,
                createdAt: m.createdAt,
            }),
        onChatListUpdate: () => {
            void refetch();
        },
    });

    // Default the selection to the most recent conversation once loaded.
    useEffect(() => {
        const first = chats[0];
        if (!selectedId && first) setSelectedId(first.id);
    }, [
        chats,
        selectedId,
    ]);

    /** The learner in a chat: its `USER` participant. See `learnerId` below for why role and not the viewer. */
    const learnerOf = (chat: (typeof chats)[number]) => chat.participants.find((p) => p.user?.role === "USER");

    /**
     * Who a conversation is named after in the list and the thread header: the learner.
     *
     * It used to be the first participant who was not the viewer, which named a staff member
     * whenever the viewer had not joined the chat or another coach or admin had — Slack's alert
     * showed the learner, and the inbox showed the admin. The viewer-relative fallback is only
     * for a chat with no learner in it at all.
     */
    const otherParticipant = (chat: (typeof chats)[number]): ChatUser | undefined =>
        learnerOf(chat)?.user ??
        chat.participants.find((p) => p.userId !== me?.id)?.user ??
        chat.participants[0]?.user ??
        undefined;

    /**
     * Messages from anyone else newer than the viewer's read pointer — the rule
     * `countUnreadMessagesForUser` applies server-side for the nav badge.
     *
     * By `createdAt`, not by position after the pointer: an automatic reply shares its trigger's
     * timestamp, so which of the two sorts last is arbitrary, and the pointer may land on either.
     *
     * Zero when the viewer is not a participant. A chat reached through the inbox gives them no read
     * pointer — `markChatRead` has no row to write — so a count here could only ever grow. The nav
     * badge leaves these chats out for the same reason.
     */
    const unreadCount = (chat: (typeof chats)[number]): number => {
        const mine = chat.participants.find((p) => p.userId === me?.id);
        if (!mine) return 0;
        const fromOthers = chat.messages.filter((m) => m.authorId !== me?.id);
        const lastViewedId = mine.latestViewedMessage?.id;
        if (!lastViewedId) return fromOthers.length;
        const viewed = chat.messages.find((m) => m.id === lastViewedId);
        if (!viewed) return 0;
        const viewedAt = new Date(viewed.createdAt).getTime();
        return fromOthers.filter((m) => new Date(m.createdAt).getTime() > viewedAt).length;
    };

    const conversations: Conversation[] = chats.map((chat) => {
        const other = otherParticipant(chat);
        const last = chat.messages[chat.messages.length - 1];
        const unread = unreadCount(chat);
        return {
            id: chat.id,
            name: chat.name || displayName(other),
            initials: initialsFor(other),
            org: organizationFor(other),
            preview: last?.body ?? "No messages yet",
            time: relativeTime(chat.updatedAt),
            unreadCount: unread > 0 ? unread : undefined,
            // A chat is "after hours" when its most recent message is the
            // auto-reply sent for messaging outside business hours — the
            // backend has already decided this, so we just read it off the
            // last message rather than re-deriving business hours here.
            status: last?.senderKind === "COACH_OFF_HOURS" ? "after_hours" : null,
        };
    });

    const filtered = conversations.filter((c) => {
        if (filter === "unread" && !c.unreadCount) return false;
        // if (filter === "after_hours" && c.status !== "after_hours") return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return c.name.toLowerCase().includes(q) || c.preview.toLowerCase().includes(q);
    });

    const filterLabels: Record<FilterTab, string> = {
        all: "All",
        unread: "Unread",
        // after_hours: "After hours",
        // crisis: "Crisis",
    };

    const selectedChat = chats.find((c) => c.id === selectedId);
    const selectedOther = selectedChat ? otherParticipant(selectedChat) : undefined;
    /*
     * Whose messages render as the learner's.
     *
     * Not the viewer: an operator reads conversations they are not part of, and anchoring on
     * `me.id` drew every message the same way whenever another coach had replied — the thread
     * collapsed into one voice with no way to tell who said what.
     *
     * `USER` is the only learner role. Everyone else in a chat displays as a coach, deliberately:
     * a partner-side ORG_ADMIN or SUB_ORG_ADMIN is on the service's side of the conversation as
     * far as the transcript is concerned, and the distinction the layout is drawing is "the
     * person being coached" against "everybody supporting them".
     *
     * `undefined` when a chat has no learner in it, which draws every message as a coach rather
     * than guessing — uniform, and honest about not knowing.
     */
    const learnerId = selectedChat ? learnerOf(selectedChat)?.userId : undefined;

    const threadMessages = selectedChat
        ? mergeMessages(
              selectedChat.messages.map((m) => ({
                  id: m.id,
                  body: m.body,
                  authorId: m.authorId ?? null,
                  senderKind: m.senderKind,
                  createdAt: m.createdAt,
              })),
              liveByChat[selectedChat.id] ?? [],
          )
        : [];

    /*
     * Mark the open conversation read — on opening it, and again as messages land in it.
     *
     * It used to happen only on a click, so the conversation selected for you on load, and a reply
     * arriving in the one you were reading, stayed unread on screen. Same shape as the web
     * ChatPage: gated on there being something to mark, with the live messages counted because the
     * cached query has not seen them yet. Only for a participant — see `unreadCount`.
     */
    const selectedIsMine = !!me?.id && !!selectedChat?.participants.some((p) => p.userId === me.id);
    const selectedHasUnread = selectedIsMine && hasUnreadToMark(selectedChat, me?.id, liveByChat[selectedId]);
    useEffect(() => {
        if (!selectedId || !me?.id || !selectedHasUnread) return;
        void markRead({ variables: { input: { chatId: selectedId, userId: me.id } } }).catch(() => {
            /* best-effort */
        });
    }, [
        selectedId,
        me?.id,
        selectedHasUnread,
    ]);

    const messagesRef = useRef<HTMLDivElement>(null);
    const prevChatIdRef = useRef<string | null>(null);
    const prevMessageCountRef = useRef(0);
    const wasNearBottomRef = useRef(true);
    useEffect(() => {
        const el = messagesRef.current;
        if (!el || !selectedChat) return;

        const switchedChat = prevChatIdRef.current !== selectedChat.id;
        const grew = threadMessages.length > prevMessageCountRef.current;
        prevChatIdRef.current = selectedChat.id;
        prevMessageCountRef.current = threadMessages.length;

        // Always jump to the bottom when opening a conversation. Otherwise, only
        // follow new messages if the user was already near the bottom.
        if (switchedChat) {
            wasNearBottomRef.current = true;
            el.scrollTo({ top: el.scrollHeight });
        } else if (grew && wasNearBottomRef.current) {
            el.scrollTo({ top: el.scrollHeight });
        }
    }, [
        selectedChat,
        threadMessages,
    ]);

    /*
     * The conversation list, shown or hidden.
     *
     * Two jobs, one flag. On a wide screen it collapses the column so the thread can use the
     * width; below `md` the two columns do not fit side by side at all, so the list becomes a
     * full-screen layer over the thread and this is which of the two you are looking at.
     *
     * Open is the honest default in both: the list is how you get to a conversation, and
     * arriving at an inbox with it hidden gives you nothing to act on.
     */
    const isMobile = useIsMobile();
    const [
        listOpen,
        setListOpen,
    ] = useState(true);

    const handleMessagesScroll = () => {
        const el = messagesRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        wasNearBottomRef.current = distanceFromBottom < 120;
    };

    const handleSelect = (chatId: string) => {
        setSelectedId(chatId);
        // Only on a narrow screen, where the list is covering the thread it just selected.
        // Collapsing the column on a wide one would take the list away for no reason.
        if (isMobile) setListOpen(false);
    };

    const allUsers = useMemo(
        () => (usersData?.users ?? []).filter((u) => u.id !== me?.id),
        [
            usersData,
            me,
        ],
    );

    const filteredUsers = useMemo(() => {
        const q = newConvSearch.toLowerCase();
        if (!q) return allUsers;
        return allUsers.filter(
            (u) =>
                u.firstName?.toLowerCase().includes(q) ||
                u.lastName?.toLowerCase().includes(q) ||
                u.email?.toLowerCase().includes(q),
        );
    }, [
        allUsers,
        newConvSearch,
    ]);

    const handleCreateChat = async () => {
        if (!newConvUserId) return;
        setNewConvCreating(true);
        try {
            const result = await createChat({ variables: { input: { recipientId: newConvUserId } } });
            const newChat = result.data?.createChat;
            if (newChat) {
                // The chat exists whatever happens here, so a failed refresh costs a stale list
                // and nothing more. Swallowed because this runs from `void handleCreateChat()`,
                // where a rejection becomes an unhandled promise rejection instead of anything a
                // user sees — and Apollo aborts in-flight queries on unmount, so closing the
                // dialog right after creating was enough to trigger one.
                try {
                    await refetch();
                } catch {
                    // Deliberately ignored — see above.
                }
                setSelectedId(newChat.id);
                setPendingScrollChatId(newChat.id);
            }
            setNewConvOpen(false);
            setNewConvSearch("");
            setNewConvUserId(null);
        } finally {
            setNewConvCreating(false);
        }
    };

    const handleSend = async () => {
        const body = messageText.trim();
        if (!body || !selectedChat) return;
        setMessageText("");

        // A conversation opened from the inbox (org-wide, via listChatsWithReply) does not make
        // the caller a participant — only starting one via New Conversation does that.
        // createChatMessage requires participation even for ADMIN/SYSTEM, so join first or the
        // reply is rejected as FORBIDDEN.
        if (me?.id && !selectedChat.participants.some((p) => p.userId === me.id)) {
            await joinChat({ variables: { chatId: selectedChat.id, userId: me.id } });
            try {
                await refetch();
            } catch {
                // Best-effort — see handleCreateChat's refetch above.
            }
        }

        const result = await sendMessage({
            variables: {
                input: { chatId: selectedChat.id, body, senderType: me?.role ?? "COACH" },
            },
        });
        const created = result.data?.createChatMessage;
        if (created) {
            appendLive(created.chatId, {
                id: created.id,
                body: created.body,
                authorId: created.authorId ?? null,
                senderKind: created.senderKind,
                createdAt: created.createdAt,
            });
        }
    };

    const [
        pendingScrollChatId,
        setPendingScrollChatId,
    ] = useState<string | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!pendingScrollChatId) return;
        const row = listRef.current?.querySelector<HTMLElement>(`[data-chat-id="${pendingScrollChatId}"]`);
        if (row) {
            row.scrollIntoView({ block: "nearest" });
            setPendingScrollChatId(null);
        }
    }, [
        pendingScrollChatId,
        chats,
    ]);

    return (
        <div className="relative flex h-[calc(100vh-3rem)] overflow-hidden">
            <div
                id="inbox-conversation-list"
                className={cn(
                    "border-border flex-col border-r",
                    "bg-background absolute inset-0 z-20 w-full",
                    "md:relative md:inset-auto md:z-auto md:w-72 md:shrink-0",
                    listOpen ? "flex" : "hidden",
                )}
            >
                <div className="border-border flex items-center justify-between border-b px-4 py-3">
                    <Text weight="semibold" className="text-base">
                        Inbox
                    </Text>
                    <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() => setListOpen(false)}
                        aria-label="Hide conversations"
                        aria-expanded={listOpen}
                        aria-controls="inbox-conversation-list"
                    >
                        <ArrowLeft className="h-4 w-4 md:hidden" />
                        <PanelLeft className="hidden h-4 w-4 md:block" />
                    </Button>
                </div>

                <div className="border-border border-b px-3 py-2">
                    <div className="relative">
                        <Search className="text-muted-foreground absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
                        <Input
                            placeholder="Search conversations"
                            className="h-8 pl-8 text-xs"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            type="text"
                        />
                    </div>
                </div>

                <div className="border-border flex items-center gap-1 border-b px-3 py-2">
                    <div className="flex items-center gap-1 overflow-x-auto">
                        {(
                            [
                                "all",
                                "unread",
                                "after_hours",
                                "crisis",
                            ] as FilterTab[]
                        ).map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setFilter(tab)}
                                className={cn(
                                    "flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors",
                                    filter === tab
                                        ? "bg-primary text-primary-foreground font-medium"
                                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                                )}
                            >
                                {filterLabels[tab]}
                            </button>
                        ))}
                    </div>
                    <Button
                        variant="outline"
                        size="icon-sm"
                        className="ml-auto shrink-0"
                        onClick={() => setNewConvOpen(true)}
                        aria-label="New conversation"
                    >
                        <Plus className="h-4 w-4" />
                    </Button>
                </div>

                <div ref={listRef} className="relative flex-1 overflow-y-auto">
                    {initialLoading && conversations.length === 0 && (
                        <div className="text-muted-foreground px-4 py-6 text-center text-xs">Loading…</div>
                    )}
                    {/*
                     * Not while more pages are still arriving. "You're all caught up" is a claim
                     * about the whole inbox, and a search that matches nothing on the first page
                     * may well match something on the third.
                     */}
                    {!initialLoading && !pagingMore && filtered.length === 0 && (
                        <div className="text-muted-foreground px-4 py-6 text-center text-xs">
                            You're all caught up! ✨
                        </div>
                    )}
                    {filtered.map((conv) => (
                        <ConversationRow
                            key={conv.id}
                            conv={conv}
                            selected={selectedId === conv.id}
                            onClick={() => handleSelect(conv.id)}
                        />
                    ))}
                </div>
            </div>

            {/* ── Column 2: Chat Thread ── */}
            <div className="flex min-w-0 flex-1 flex-col">
                <div className="border-border flex items-center justify-between border-b px-5 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                        {!listOpen && (
                            <Button
                                variant="outline"
                                size="icon-sm"
                                className="shrink-0"
                                onClick={() => setListOpen(true)}
                                aria-label="Show conversations"
                                aria-expanded={listOpen}
                                aria-controls="inbox-conversation-list"
                            >
                                <PanelLeft className="h-4 w-4" />
                            </Button>
                        )}
                        <Avatar size="sm" className="shrink-0">
                            <AvatarGradientFallback>{initialsFor(selectedOther)}</AvatarGradientFallback>
                        </Avatar>
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-x-2">
                                <Text weight="semibold" className="truncate text-sm">
                                    {selectedChat?.name || displayName(selectedOther)}
                                </Text>
                                <Text className="text-muted-foreground truncate text-xs">
                                    {selectedOther?.email ?? ""}
                                </Text>
                                <Text className="text-muted-foreground truncate text-xs">
                                    · {organizationFor(selectedOther)}
                                </Text>
                            </div>
                            <div className="flex items-center gap-2">
                                <Mono className="text-muted-foreground text-[11px]">
                                    {selectedChat
                                        ? `${threadMessages.length} ${threadMessages.length === 1 ? "message" : "messages"}`
                                        : ""}
                                </Mono>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Messages */}
                <div
                    ref={messagesRef}
                    onScroll={handleMessagesScroll}
                    className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-5"
                >
                    {!selectedChat && (
                        <div className="text-muted-foreground flex flex-1 items-center justify-center text-sm">
                            Select a conversation
                        </div>
                    )}
                    {threadMessages.map((msg, i) => (
                        <React.Fragment key={msg.id}>
                            {isNewDay(msg.createdAt, threadMessages[i - 1]?.createdAt) && (
                                <ChatDateDivider iso={msg.createdAt} />
                            )}
                            <MessageBubble
                                // The operator is the viewer here, so their own replies sit on the
                                // right and the learner on the left, the way every inbox reads.
                                // `apps/web` keeps the default, where the learner is the viewer.
                                perspective="operator"
                                msg={{
                                    id: msg.id,
                                    // The learner, not the viewer: `resolveMessageSender` still
                                    // answers which *role* wrote the message. Only which side that
                                    // role is drawn on depends on who is looking.
                                    sender: resolveMessageSender(msg, learnerId),
                                    body: msg.body,
                                    time: messageTime(msg.createdAt),
                                }}
                            />
                        </React.Fragment>
                    ))}
                </div>

                {/* Composer */}
                <div className="border-border border-t px-4 py-3">
                    <div className="border-border flex items-end gap-2 rounded-xl border px-3 py-2">
                        <AutoGrowTextarea
                            className="text-foreground placeholder:text-muted-foreground/50 max-h-48 min-h-0 flex-1 rounded-none border-none bg-transparent px-0 py-1.5 text-sm focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                            placeholder="Type a message…"
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    void handleSend();
                                }
                            }}
                        />
                        <div className="flex shrink-0 items-center gap-2">
                            <Button
                                size="sm"
                                className="gap-1.5"
                                disabled={!messageText.trim() || !selectedChat}
                                onClick={() => void handleSend()}
                            >
                                Send
                                <Send className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>
                    {/*
                     * Only in a real conversation with a known recipient: the mutation derives who
                     * gets the card from chat membership and refuses a group chat, so offering the
                     * control without one would just produce an error.
                     */}
                    {selectedChat && selectedOther && meData?.currentUser?.role === "ADMIN" && (
                        <div className="mt-2 px-1">
                            <GiftCardButton
                                chatId={selectedChat.id}
                                recipientName={displayName(selectedOther)}
                                onSent={(message) =>
                                    appendLive(message.chatId, {
                                        id: message.id,
                                        body: message.body,
                                        authorId: message.authorId ?? null,
                                        senderKind: message.senderKind,
                                        createdAt: message.createdAt,
                                    })
                                }
                            />
                        </div>
                    )}
                    <div className="mt-1.5 flex items-center justify-between px-1 py-1">
                        <Mono className="text-muted-foreground text-[11px]">
                            Replies are sent as {displayName(me ?? undefined)}
                        </Mono>
                        <Mono className="text-muted-foreground text-[11px]">{liveEnabled ? "live" : "auto-saved"}</Mono>
                    </div>
                </div>
            </div>

            {/* New conversation dialog */}
            <Dialog
                open={newConvOpen}
                onOpenChange={(open) => {
                    setNewConvOpen(open);
                    if (!open) {
                        setNewConvSearch("");
                        setNewConvUserId(null);
                    }
                }}
            >
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>New conversation</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-3">
                        <Input
                            placeholder="Search by name or email…"
                            value={newConvSearch}
                            onChange={(e) => {
                                setNewConvSearch(e.target.value);
                                setNewConvUserId(null);
                            }}
                            autoFocus
                            type="text"
                        />
                        <div className="flex max-h-60 flex-col overflow-y-auto rounded-md border">
                            {filteredUsers.length === 0 && (
                                <div className="text-muted-foreground px-3 py-4 text-center text-xs">
                                    No users found
                                </div>
                            )}
                            {filteredUsers.map((u) => {
                                const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email || u.id;
                                const selected = newConvUserId === u.id;
                                return (
                                    <button
                                        key={u.id}
                                        onClick={() => setNewConvUserId(selected ? null : u.id)}
                                        className={cn(
                                            "flex items-center gap-3 border-b px-3 py-2.5 text-left text-sm last:border-b-0",
                                            selected
                                                ? "bg-primary/10 text-primary"
                                                : "hover:bg-muted transition-colors",
                                        )}
                                    >
                                        <Avatar size="sm" className="shrink-0">
                                            <AvatarGradientFallback>{initialsFor(u)}</AvatarGradientFallback>
                                        </Avatar>
                                        <div className="min-w-0">
                                            <div className="truncate font-medium">{name}</div>
                                            {u.email && (
                                                <div className="text-muted-foreground truncate text-xs">{u.email}</div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNewConvOpen(false)}>
                            Cancel
                        </Button>
                        <Button disabled={!newConvUserId || newConvCreating} onClick={() => void handleCreateChat()}>
                            {newConvCreating ? "Starting…" : "Start chat"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
